import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button } from '@airia/ui';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Slider } from '../../ui/slider';
import { ArrowLeft, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { LoadingSpinner } from '../../ui/loading-spinner';
import DraftPreview from './DraftPreview';
import { formatEmailBody } from '../../lib/formatEmailBody';
import { parseEmailTags } from '../../lib/emailParser';
import { logger } from '../../lib/logger';
const STORAGE_KEY = 'airia-new-email-preferences';
const NewEmailDraftForm = ({ onBack, onGenerate }) => {
    // Form fields
    const [to, setTo] = useState('');
    const [subject, setSubject] = useState('');
    const [messageIdea, setMessageIdea] = useState('');
    // Options
    const [wordCount, setWordCount] = useState(100);
    const [tone, setTone] = useState('neutral');
    const [style, setStyle] = useState('formal');
    const [showOptions, setShowOptions] = useState(false);
    // Response state
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiResponse, setAiResponse] = useState(null);
    const [generatedSubject, setGeneratedSubject] = useState(null);
    const [error, setError] = useState(null);
    // Load saved preferences on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const prefs = JSON.parse(saved);
                setWordCount(prefs.wordCount || 100);
                setTone(prefs.tone || 'neutral');
                setStyle(prefs.style || 'formal');
            }
        }
        catch (error) {
            console.error('Failed to load new email preferences:', error);
        }
    }, []);
    const savePreferences = () => {
        const prefs = {
            wordCount,
            tone,
            style,
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        }
        catch (error) {
            console.error('Failed to save new email preferences:', error);
        }
    };
    const buildNewEmailPrompt = () => {
        let prompt = 'Write a new email';
        if (to.trim()) {
            prompt += ` to ${to.trim()}`;
        }
        if (subject.trim()) {
            prompt += ` with the subject "${subject.trim()}"`;
        }
        if (messageIdea.trim()) {
            prompt += ` that ${messageIdea.trim()}`;
        }
        if (wordCount < 200) {
            prompt += `. Keep it around ${wordCount} words`;
        }
        else {
            prompt += `. Make it as long as needed`;
        }
        prompt += `. Use a ${tone} tone`;
        prompt += ` and ${style} communication style`;
        // If no subject provided, ask AI to suggest one
        if (!subject.trim()) {
            prompt += `. Also suggest an appropriate subject line`;
        }
        return prompt;
    };
    const handleGenerate = () => {
        if (!messageIdea.trim())
            return;
        savePreferences();
        setIsGenerating(true);
        setAiResponse(null);
        setGeneratedSubject(null);
        setError(null);
        const prompt = buildNewEmailPrompt();
        onGenerate(prompt, (response) => {
            setIsGenerating(false);
            // Try to parse email tags for subject
            const parts = parseEmailTags(response);
            const emailPart = parts.find((p) => p.type === 'email' && p.emailData);
            if (emailPart && emailPart.emailData) {
                setGeneratedSubject(emailPart.emailData.subject || subject);
                setAiResponse(emailPart.emailData.body);
            }
            else {
                setGeneratedSubject(subject);
                setAiResponse(response);
            }
        });
    };
    const handleRegenerate = () => {
        handleGenerate();
    };
    const handleOpenNewEmail = async (editedContent, editedSubject) => {
        setError(null);
        try {
            if (typeof Office !== 'undefined' && Office.context.mailbox) {
                const displayNewMessageForm = Office.context.mailbox.displayNewMessageForm;
                if (displayNewMessageForm) {
                    const emailOptions = {
                        htmlBody: formatEmailBody(editedContent),
                    };
                    // Add recipients if provided
                    if (to.trim()) {
                        const recipients = to
                            .split(',')
                            .map((email) => email.trim())
                            .filter((email) => email);
                        if (recipients.length > 0) {
                            emailOptions.toRecipients = recipients;
                        }
                    }
                    // Add subject if provided
                    const finalSubject = editedSubject || generatedSubject || subject;
                    if (finalSubject) {
                        emailOptions.subject = finalSubject;
                    }
                    displayNewMessageForm(emailOptions);
                }
                else {
                    logger.error('displayNewMessageForm not available');
                    setError('New email function not available');
                }
            }
            else {
                logger.error('Office context not available');
                setError('Office context not available');
            }
        }
        catch (err) {
            logger.error('Error creating new email:', err);
            setError('Failed to open new email');
        }
    };
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    // If we have a response, show the preview
    if (aiResponse) {
        return (_jsxs("div", { className: "my-4", children: [_jsxs("div", { className: "flex items-center gap-2 mb-4", children: [_jsxs(Button, { variant: "ghost", size: "sm", onClick: () => setAiResponse(null), className: "h-8 px-2", children: [_jsx(ArrowLeft, { className: "size-4 mr-1" }), "Edit"] }), _jsx("span", { className: "text-sm font-medium text-foreground", children: "Draft New Email" })] }), (to || generatedSubject) && (_jsxs("div", { className: "p-3 bg-muted/50 rounded-lg mb-3 text-sm", children: [to && (_jsxs("div", { className: "text-muted-foreground", children: [_jsx("span", { className: "font-medium", children: "To:" }), " ", to] })), generatedSubject && (_jsxs("div", { className: "text-muted-foreground", children: [_jsx("span", { className: "font-medium", children: "Subject:" }), " ", generatedSubject] }))] })), _jsx(DraftPreview, { content: aiResponse, subject: generatedSubject || subject, onRegenerate: handleRegenerate, onOpenInOutlook: handleOpenNewEmail, isLoading: isGenerating, variant: "new-email", error: error })] }));
    }
    return (_jsxs("div", { className: "my-4 space-y-4", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Button, { variant: "ghost", size: "sm", onClick: onBack, className: "h-8 px-2", children: [_jsx(ArrowLeft, { className: "size-4 mr-1" }), "Back"] }), _jsx("span", { className: "text-sm font-medium text-foreground", children: "Draft New Email" })] }), _jsxs("div", { className: "space-y-2", children: [_jsxs("label", { className: "text-sm font-medium text-foreground", children: ["To ", _jsx("span", { className: "text-muted-foreground font-normal", children: "(optional)" })] }), _jsx(Input, { type: "email", placeholder: "recipient@example.com", value: to, onChange: (e) => setTo(e.target.value), disabled: isGenerating })] }), _jsxs("div", { className: "space-y-2", children: [_jsxs("label", { className: "text-sm font-medium text-foreground", children: ["Subject ", _jsx("span", { className: "text-muted-foreground font-normal", children: "(optional)" })] }), _jsx(Input, { type: "text", placeholder: "Email subject (AI will suggest one if left blank)", value: subject, onChange: (e) => setSubject(e.target.value), disabled: isGenerating })] }), _jsxs("div", { className: "space-y-2", children: [_jsxs("label", { className: "text-sm font-medium text-foreground", children: ["What would you like to say? ", _jsx("span", { className: "text-destructive", children: "*" })] }), _jsx(Textarea, { placeholder: "e.g., Follow up on our meeting and schedule a call for next week", value: messageIdea, onChange: (e) => setMessageIdea(e.target.value), className: "min-h-[80px] resize-none", disabled: isGenerating })] }), _jsxs("div", { className: "border border-border rounded-lg", children: [_jsxs("button", { type: "button", onClick: () => setShowOptions(!showOptions), className: "w-full flex items-center justify-between p-3 text-sm font-medium text-foreground hover:bg-accent/50 rounded-lg transition-colors", children: [_jsx("span", { children: "Options" }), showOptions ? (_jsx(ChevronUp, { className: "size-4 text-muted-foreground" })) : (_jsx(ChevronDown, { className: "size-4 text-muted-foreground" }))] }), showOptions && (_jsxs("div", { className: "p-3 pt-0 space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Email Length" }), _jsx(Slider, { value: [wordCount], onValueChange: ([value]) => setWordCount(value), min: 30, max: 200, step: 10, className: "w-full" }), _jsx("div", { className: "text-xs text-muted-foreground", children: wordCount === 200 ? '200+ words' : `${wordCount} words` })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Tone" }), _jsx("div", { className: "flex gap-2", children: ['positive', 'neutral', 'negative'].map((t) => (_jsx(Button, { variant: tone === t ? 'default' : 'outline', size: "sm", onClick: () => setTone(t), className: "flex-1", disabled: isGenerating, children: capitalize(t) }, t))) })] }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-foreground", children: "Style" }), _jsx("div", { className: "flex gap-2", children: ['formal', 'informal', 'factual'].map((s) => (_jsx(Button, { variant: style === s ? 'default' : 'outline', size: "sm", onClick: () => setStyle(s), className: "flex-1", disabled: isGenerating, children: capitalize(s) }, s))) })] })] }))] }), _jsx(Button, { onClick: handleGenerate, className: "w-full", disabled: isGenerating || !messageIdea.trim(), children: isGenerating ? (_jsxs("span", { className: "flex items-center gap-2", children: [_jsx(LoadingSpinner, {}), "Generating..."] })) : (_jsxs(_Fragment, { children: [_jsx(Send, { className: "size-4 mr-2" }), "Generate Draft"] })) }), error && (_jsx("div", { className: "p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive", children: error }))] }));
};
export default NewEmailDraftForm;
